import React, { useEffect, useState } from "react";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import { portalApi } from "../../lib/portalApi";

const TARGET_SLUG = "4601-palm-tower";

const CONFIRM_TEXT =
  "Apply verified demolition correction (95%)?\n\n" +
  "• Sets demolition / current site phase progress to the client-confirmed 95%\n" +
  "• Waste clearance & disposal rows stay INCOMPLETE (capped at 90%)\n" +
  "• The M2 milestone is raised to 95% — never marked complete\n" +
  "• Writes an audit log entry (portal_data_fix_log)\n" +
  "• Sends NO client email\n\n" +
  "The operation is idempotent — confirming once is enough.";

export default function DemolitionCorrectionControl({ slug, isAdmin }) {
  const enabled = isAdmin && slug === TARGET_SLUG;
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!enabled) return;
    portalApi.demolitionSyncStatus(slug).then(setStatus).catch(() => setStatus(null));
  }, [slug, enabled]);

  if (!enabled) return null;
  const applied = !!status?.applied;

  const apply = async () => {
    if (!window.confirm(CONFIRM_TEXT)) return;
    setBusy(true); setError(""); setResult("");
    try {
      const r = await portalApi.demolitionSyncApply(slug);
      setResult(
        `Correction applied — Demolition now ${r.demolition_percent}% · overall programme ${r.overall_percent}% · ` +
        `${(r.changed_tasks || []).length} activit${(r.changed_tasks || []).length === 1 ? "y" : "ies"} and ` +
        `${(r.changed_milestones || []).length} milestone(s) updated. No client email was sent.`
      );
      setStatus(await portalApi.demolitionSyncStatus(slug));
    } catch (e) {
      setError(e.message || "Correction failed — no changes were applied.");
    }
    setBusy(false);
  };

  return (
    <div className="mt-3 border border-[#A6822F]/30 bg-[#FAF5EC]/70 px-3 py-2.5" data-testid="demolition-correction-control">
      <div className="flex items-center flex-wrap gap-2">
        <button
          type="button"
          onClick={apply}
          disabled={busy || applied || !status}
          data-testid="demolition-correction-apply-btn"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border transition-colors disabled:cursor-not-allowed ${
            applied
              ? "border-emerald-600/40 text-emerald-700 bg-emerald-600/10"
              : "border-[#6E1F2C]/40 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white disabled:opacity-60"
          }`}
        >
          {applied ? <CheckCircle2 size={11} /> : <ShieldCheck size={11} />}
          {applied ? "Applied — demolition correction (95%)" : busy ? "Applying…" : "Apply verified demolition correction (95%)"}
        </button>
        {applied && status?.last?.at && (
          <span className="text-[9px] text-[#7A6B5F]" data-testid="demolition-correction-applied-at">
            on {String(status.last.at).slice(0, 10)} · demolition {status.last.demolition_percent}%
          </span>
        )}
        <span className="text-[9px] text-[#7A6B5F]">Main-admin only · idempotent · no client email</span>
      </div>
      {result && <p className="mt-1.5 text-[10px] text-emerald-700" data-testid="demolition-correction-result">{result}</p>}
      {error && <p className="mt-1.5 text-[10px] text-[#A3141A]" data-testid="demolition-correction-error">{error}</p>}
    </div>
  );
}
